import { motion } from 'framer-motion';
import { Check, Sparkles, Shield, Clock, Droplets, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHero from '../components/PageHero';

const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const MembershipsPage = () => {
  const benefits = [
    { icon: Droplets, title: 'Unlimited Washes', desc: 'Wash as often as you want, every single day if you need.' },
    { icon: Shield, title: 'No Contracts', desc: 'Month-to-month membership. Cancel anytime with no penalties.' },
    { icon: Clock, title: 'Save Time', desc: 'Skip the payment step - just drive up and wash.' },
    { icon: Sparkles, title: 'Premium Quality', desc: 'Access to our best wash packages with every visit.' },
    { icon: Star, title: 'Member Benefits', desc: 'Exclusive deals and perks for EverWash members.' },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <PageHero 
        badge="EverWash Membership"
        title="Unlimited"
        titleAccent="Car Washes"
        subtitle="Wash your car as many times as you want for one low monthly price. Starting at just $20/month."
      />

      {/* How It Works */}
      <section className="bg-white py-20 md:py-32" data-testid="memberships-how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Simple & Easy
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              How EverWash Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Download & Sign Up',
                desc: 'Get the EverWash app and choose your membership plan. Registration takes just minutes.'
              },
              {
                step: '02',
                title: 'Drive Up & Scan',
                desc: 'When you arrive, open the app and scan at the pay station. Your membership is automatically recognized.'
              },
              {
                step: '03',
                title: 'Wash Unlimited',
                desc: 'Drive through as many times as you want. There\'s no daily limit - wash whenever you need.'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.15 }}
                className="relative"
              >
                <div className="text-[120px] font-black text-slate-100 absolute -top-8 left-0 leading-none select-none">
                  {item.step}
                </div>
                <div className="relative z-10 pt-16">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-[#0F172A] py-20 md:py-32 relative overflow-hidden" data-testid="memberships-pricing">
        {/* Animated background */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 40%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 40%)'
          }}
          animate={{
            background: [
              'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 40%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 40%)',
              'radial-gradient(circle at 80% 80%, #DC2626 0%, transparent 40%), radial-gradient(circle at 20% 20%, #3B82F6 0%, transparent 40%)',
              'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 40%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 40%)'
            ]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Membership Plans
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-6">
              Choose Your Plan
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              All plans include unlimited washes with no contracts. Cancel anytime.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Super Wash',
                price: '$26',
                single: '$16',
                features: ['Unlimited Super washes', 'Weather Shield', 'Premium Tire Shine', 'Carnauba Wax', 'Triple Foam Polish', 'Basic Wash', 'Spot Free Rinse', 'Rims Cleaned'],
                featured: true
              },
              {
                name: 'Ultra Wash',
                price: '$23',
                single: '$13',
                features: ['Unlimited Ultra washes', 'Carnauba Wax', 'Triple Foam Polish', 'Basic Wash', 'Spot Free Rinse', 'Rims Cleaned'],
                featured: false
              },
              {
                name: 'Express Wash',
                price: '$20',
                single: '$10',
                features: ['Unlimited Express washes', 'Basic Wash', 'Spot Free Rinse', 'Rims Cleaned'],
                featured: false
              }
            ].map((plan, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.featured 
                    ? 'bg-white text-[#0F172A] membership-card-featured' 
                    : 'bg-white/5 text-white border border-white/10'
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#DC2626] text-white text-sm font-bold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className={`text-2xl font-bold mb-2 ${plan.featured ? 'text-[#1E3A8A]' : ''}`}>
                  {plan.name}
                </h3>
                <div className="mb-6">
                  <span className={`text-4xl font-black ${plan.featured ? 'text-[#0F172A]' : ''}`}>
                    {plan.price}
                  </span>
                  <span className={plan.featured ? 'text-slate-500' : 'text-slate-400'}>/month</span>
                  <p className={`text-sm mt-1 ${plan.featured ? 'text-slate-400' : 'text-slate-500'}`}>
                    Single wash: {plan.single}
                  </p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 ${plan.featured ? 'text-green-500' : 'text-red-500'}`} />
                      <span className={plan.featured ? 'text-slate-600' : 'text-slate-300'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid={`membership-plan-${plan.name.toLowerCase().replace(' ', '-')}-cta`}>
                  <Button 
                    className={`w-full font-bold py-3 rounded-full ${
                      plan.featured 
                        ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white' 
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    Get Started
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>

          <motion.p {...fadeInUp} className="text-center text-slate-400 mt-8 text-sm">
            * Prices may vary. View current pricing on EverWash app.
          </motion.p>
          <motion.p {...fadeInUp} className="text-center text-slate-500 mt-3 text-xs max-w-2xl mx-auto">
            Memberships are managed and billed by <a href="https://www.everwash.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">EverWash, Inc.</a>, a third-party provider. Super Spray Car Wash II is a participating wash location. 
            Cancellation, plan changes, and billing inquiries are handled through the EverWash app. 
            See EverWash's <a href="https://www.everwash.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">terms of service</a> and <a href="https://www.everwash.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">privacy policy</a> for full details.
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-20 md:py-32" data-testid="memberships-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Member Benefits
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              Why Go Unlimited?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Math Section */}
      <section className="bg-slate-50 py-20 md:py-32" data-testid="memberships-math">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              The Math
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-8">
              It Just Makes Sense
            </h2>
            
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="text-center p-6 bg-slate-50 rounded-xl">
                  <p className="text-slate-500 mb-2">Single Wash</p>
                  <p className="text-3xl font-black text-[#0F172A]">$10 - $16</p>
                  <p className="text-sm text-slate-400 mt-2">Per visit</p>
                </div>
                <div className="text-center p-6 bg-[#1E3A8A] rounded-xl">
                  <p className="text-blue-200 mb-2">EverWash Membership</p>
                  <p className="text-3xl font-black text-white">$20</p>
                  <p className="text-sm text-blue-200 mt-2">Unlimited washes</p>
                </div>
              </div>
              
              <p className="text-lg text-slate-600 mb-8">
                Wash just <span className="font-bold text-[#0F172A]">twice a month</span> and you're already saving. Most members wash 4-6 times per month - that's over <span className="font-bold text-[#DC2626]">$40 in savings</span>!
              </p>

              <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="memberships-math-cta">
                <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-lg px-10 py-6 rounded-full btn-primary">
                  Start Saving Today <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-32" data-testid="memberships-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Questions?
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mb-6">
              Frequently Asked
            </h2>
          </motion.div>

          <motion.div {...fadeInUp} className="space-y-6">
            {[
              {
                q: 'How often can I wash my car?',
                a: 'As often as you want! There are no daily limits. Many members wash every day or every other day.'
              },
              {
                q: 'Is there a contract?',
                a: 'No contracts. EverWash is month-to-month. You can cancel anytime through the app with no penalties.'
              },
              {
                q: 'How do I use my membership?',
                a: 'Download the EverWash app, select Super Spray Car Wash II, and scan the QR code at the pay station. It\'s that easy!'
              },
              {
                q: 'Can I use my membership at other locations?',
                a: 'Your membership is specifically for Super Spray Car Wash II. EverWash has other partner locations if you travel.'
              },
              {
                q: 'What if I want to upgrade or downgrade?',
                a: 'You can change your plan anytime through the EverWash app. Changes take effect on your next billing cycle.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="border-b border-slate-200 pb-6">
                <h3 className="font-bold text-[#0F172A] mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#DC2626] py-20 md:py-24 relative overflow-hidden" data-testid="memberships-cta">
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
          animate={{ x: [0, 60], y: [0, 60] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Ready to Go Unlimited?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Join thousands of members who wash smarter, not harder. Sign up for EverWash today.
            </p>
            <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="memberships-final-cta">
              <Button className="bg-white text-[#DC2626] hover:bg-slate-100 font-bold text-lg px-10 py-6 rounded-full">
                Sign Up for EverWash
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MembershipsPage;
